/* eslint-disable camelcase */
import { SendArticle } from 'src/modules/sending/models/sendArticle';

// Rena prisfunktioner för utskickets sammanfattning. Prismodellen speglar FS.Ekonomi/Core/Helpers/SendItemArticle.cs:
// - E-post är gratis, PDF-nedladdning har ingen sändkostnad.
// - Kivra prissätts via artiklar med artnr som börjar "KIV".
// - Brev prissätts via artikelfamilj som beror på utskickstyp (medlem/påminnelse) + färgval (svartvitt/färg).
// Alla artiklar är volym-bracketade: raden vars [receivers_min, receivers_max] rymmer antalet gäller (max === 0 = öppet uppåt).

export const VAT_RATE = 0.25;
export const KIVRA_PREFIX = 'KIV';
// Fast arbetskostnad per beställning: artikel SU0101 (arbetstid), 0,5 timmar. Timmarna visas inte för kunden – bara "Arbetskostnad".
export const LABOR_ARTNR = 'SU0101';
export const LABOR_HOURS = 0.5;

// Brev-artikelns prefix utifrån utskickstyp och färgval (samma familjer som SendItemArticle.cs).
export const brevArticlePrefix = (isReminder: boolean, isColor: boolean): string => {
  if (isReminder) return isColor ? 'MA024' : 'MA021';
  return isColor ? 'MA014' : 'MA011';
};

export interface BracketPrice {
  artnr: string;
  name: string;
  price: number;
}

// Hittar artikeln vars artnr börjar med prefix och vars antalsintervall rymmer antalet. null om ingen matchar.
export const pickBracketPrice = (articles: SendArticle[], prefix: string, count: number): BracketPrice | null => {
  if (count <= 0) return null;
  const match = articles.find(
    (a) =>
      typeof a.artnr === 'string' && a.artnr.startsWith(prefix) && count >= a.receivers_min && (a.receivers_max === 0 || count <= a.receivers_max),
  );
  if (!match) return null;
  return { artnr: match.artnr, name: match.name, price: match.price };
};

export interface PriceLine {
  key: string;
  label: string;
  count: number;
  // Styckpris. null = gratis kanal utan pris (e-post/PDF) eller att pris inte kunde hittas (se priceMissing).
  unitPrice: number | null;
  articleName?: string;
  articleNr?: string;
  // Gratis kanal (e-post) eller ingen sändkostnad (PDF-nedladdning).
  free: boolean;
  // Prissatt kanal (Kivra/brev) där ingen artikel matchade antalet – visa platshållare istället för belopp.
  priceMissing: boolean;
  total: number;
  // Dölj antals-/styckpris-detaljen på raden (t.ex. arbetskostnaden visas bara som "Arbetskostnad – xxx kr").
  hideDetail?: boolean;
}

export interface PricingResult {
  lines: PriceLine[];
  net: number;
  vat: number;
  gross: number;
}

const LABELS: Record<string, string> = { mail: 'E-post', kivra: 'Kivra', letter: 'Brev', pdf: 'PDF-nedladdning' };

// Bygger kostnadsrader per kanal + summor (netto, moms, totalt) utifrån antal per kanal och tillgängliga artiklar.
export const computePricing = (
  channelCounts: { key: string; count: number }[],
  articles: SendArticle[],
  isReminder: boolean,
  isColor: boolean,
): PricingResult => {
  const lines: PriceLine[] = channelCounts.map(({ key, count }) => {
    const label = LABELS[key] ?? key;

    // Gratis kanaler: e-post (0 kr) och PDF-nedladdning (kunden laddar ner själv, ingen sändkostnad).
    if (key === 'mail' || key === 'pdf') {
      return { key, label, count, unitPrice: 0, free: true, priceMissing: false, total: 0 };
    }

    const prefix = key === 'kivra' ? KIVRA_PREFIX : brevArticlePrefix(isReminder, isColor);
    const bracket = pickBracketPrice(articles, prefix, count);

    if (!bracket) {
      return { key, label, count, unitPrice: null, free: false, priceMissing: true, total: 0 };
    }

    return {
      key,
      label,
      count,
      unitPrice: bracket.price,
      articleName: bracket.name,
      articleNr: bracket.artnr,
      free: false,
      priceMissing: false,
      total: bracket.price * count,
    };
  });

  // Fast arbetskostnad (SU0101, 0,5 h) läggs alltid till. Timmarna döljs – raden visas bara som "Arbetskostnad – xxx kr".
  const laborArticle = articles.find((a) => a.artnr === LABOR_ARTNR);
  if (laborArticle) {
    lines.push({
      key: 'labor',
      label: 'Arbetskostnad',
      count: 1,
      unitPrice: laborArticle.price,
      articleName: laborArticle.name,
      articleNr: laborArticle.artnr,
      free: false,
      priceMissing: false,
      total: laborArticle.price * LABOR_HOURS,
      hideDetail: true,
    });
  }

  const net = lines.reduce((sum, l) => sum + l.total, 0);
  const vat = net * VAT_RATE;
  const gross = net + vat;

  return { lines, net, vat, gross };
};
